import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';

function Classification() {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [processingStatus, setProcessingStatus] = useState('');
  const [statusType, setStatusType] = useState('info'); // 'info' | 'success' | 'danger' | 'warning'
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const pollingRef = useRef(null);
  const timerRef = useRef(null);
  const lastTribeRef = useRef(0);         // Track last observed tribe #
  const lastChangeTimeRef = useRef(0);    // When tribe # last changed

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please upload a valid Excel file (.xlsx or .xls)');
        e.target.value = '';
        return;
      }
      
      // Validate file size (max 10MB for safety)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert(`File is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 10MB.\n\nPlease use a smaller file or contact support for larger files.`);
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
      setDownloadLinks([]); // Clear previous results
      setProcessingStatus('');
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:*/*;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const stopPolling = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const startElapsedTimer = () => {
    setElapsedSeconds(0);
    timerRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
  };

  const MAX_POLL_MINUTES = 15;
  const STALE_TIMEOUT_SEC = 90; // If no tribe progress for 90s, warn

  const pollForResult = (jobId, statusUrl) => {
    let pollCount = 0;
    const maxPolls = (MAX_POLL_MINUTES * 60) / 5;
    let consecutiveErrors = 0;

    lastTribeRef.current = 0;
    lastChangeTimeRef.current = Date.now();

    pollingRef.current = setInterval(async () => {
      pollCount++;

      // Safety cut-off
      if (pollCount > maxPolls) {
        stopPolling();
        setProcessingStatus(`⚠️ Processing exceeded ${MAX_POLL_MINUTES} minutes. The job may still be running — try refreshing later.`);
        setStatusType('warning');
        setIsLoading(false);
        return;
      }

      try {
        let status;
        if (statusUrl) {
          // Poll the pre-signed S3 URL directly (no API Gateway needed)
          const res = await fetch(statusUrl, { cache: 'no-store' });
          if (!res.ok) {
            throw new Error(`S3 status HTTP ${res.status}`);
          }
          status = await res.json();
        } else {
          status = await apiCall(`/tribe-classification/status/${jobId}`);
        }
        consecutiveErrors = 0; // reset on success

        if (status.state === 'COMPLETE') {
          stopPolling();
          setDownloadLinks(status.download_links || []);
          setProcessingStatus(
            `✅ Processed ${status.tribes_processed} of ${status.total_tribes || status.tribes_processed} tribe(s). Reports ready!`
          );
          setStatusType('success');
          setIsLoading(false);

        } else if (status.state === 'FAILED') {
          stopPolling();
          setProcessingStatus(`❌ Processing failed: ${status.error || 'Unknown error'}`);
          setStatusType('danger');
          setIsLoading(false);

        } else if (status.state === 'PROCESSING') {
          // Show live tribe progress
          const currentTribe = status.current_tribe || 0;
          const totalTribes = status.total_tribes || '?';
          const tribeName = status.tribe_name || '';

          if (currentTribe !== lastTribeRef.current) {
            lastTribeRef.current = currentTribe;
            lastChangeTimeRef.current = Date.now();
          }

          if (currentTribe > 0) {
            setProcessingStatus(
              `⏳ Processing tribe ${currentTribe} of ${totalTribes}: "${tribeName}"`
            );
          } else {
            setProcessingStatus('⏳ Starting report generation… please wait.');
          }

          // Stale detection: if no tribe change for STALE_TIMEOUT_SEC, warn
          const staleSec = (Date.now() - lastChangeTimeRef.current) / 1000;
          if (staleSec > STALE_TIMEOUT_SEC && currentTribe > 0) {
            setProcessingStatus(
              `⚠️ Tribe ${currentTribe} of ${totalTribes} ("${tribeName}") is taking unusually long. ` +
              `The Lambda timeout may be too short — please check Lambda configuration.`
            );
            setStatusType('warning');
          }
        }
      } catch (err) {
        consecutiveErrors++;
        console.warn(`[Poll] Error #${consecutiveErrors}:`, err.message);

        // After 10 consecutive errors (~50s), give up
        if (consecutiveErrors >= 10) {
          stopPolling();
          setProcessingStatus(
            `❌ Lost connection to the server after ${consecutiveErrors} failed checks. ` +
            `The job may still be running — try refreshing the page.`
          );
          setStatusType('danger');
          setIsLoading(false);
        }
      }
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      alert('Please select an Excel file before submitting.');
      return;
    }

    stopPolling();
    setIsLoading(true);
    setDownloadLinks([]);
    setStatusType('info');
    setProcessingStatus('Reading file…');
    startElapsedTimer();

    try {
      const fileContent = await convertFileToBase64(selectedFile);
      setProcessingStatus('Uploading file to server…');

      const data = await apiCall('/tribe-classification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_content: fileContent, filename: selectedFile.name }),
      });

      if (data.error && !data.job_id) {
        // Pure error — no processing started
        stopPolling();
        setProcessingStatus(`❌ ${data.error}`);
        setStatusType('danger');
        setIsLoading(false);
        alert(`Error: ${data.error}${data.details ? '\n\nDetails: ' + data.details : ''}`);
        return;
      }

      // Case 1: Async path — backend returned job_id + status_url, worker running separately
      if (data.state === 'PROCESSING' && data.job_id && data.status_url) {
        setProcessingStatus('⏳ Job submitted. Waiting for processing to begin…');
        pollForResult(data.job_id, data.status_url);
        return;
      }

      // Case 2: Sync fallback completed — backend processed everything and returned results
      if (data.state === 'COMPLETE' && data.download_links) {
        stopPolling();
        setDownloadLinks(data.download_links);
        setProcessingStatus(
          `✅ Processed ${data.tribes_processed} tribe(s). Reports ready!`
        );
        setStatusType('success');
        setIsLoading(false);
        return;
      }

      // Case 3: Sync fallback failed
      if (data.state === 'FAILED') {
        stopPolling();
        setProcessingStatus(`❌ ${data.error || 'Processing failed on the server.'}`);
        setStatusType('danger');
        setIsLoading(false);
        return;
      }

      // Case 4: Legacy backend (old Lambda) — returned download_links directly without state
      if (data.download_links) {
        stopPolling();
        setDownloadLinks(data.download_links);
        setProcessingStatus(`✅ Processed ${data.tribes_processed || '?'} tribe(s). Reports ready!`);
        setStatusType('success');
        setIsLoading(false);
        return;
      }

      // Unknown response
      console.warn('Unexpected response:', data);
      setProcessingStatus('❌ Unexpected response from server.');
      setStatusType('danger');
      setIsLoading(false);
      stopPolling();

    } catch (error) {
      console.error('Classification Error:', error);
      stopPolling();

      const msg = error.message || '';
      let userMessage;

      if (msg.includes('504') || msg.includes('Gateway Timeout') || msg.includes('timed out')) {
        userMessage = 'The server timed out. The Lambda timeout is likely too short — set it to 15 minutes in AWS Console.';
      } else if (msg.includes('502')) {
        userMessage = 'Server error (502). The request may be too large or the Lambda crashed. Try a smaller file.';
      } else if (msg.includes('413') || msg.includes('too large') || msg.includes('payload')) {
        userMessage = 'File is too large for the API. Try a file under 4MB.';
      } else if (msg.includes('403')) {
        userMessage = 'Access denied. You may not have permission.';
      } else if (msg.includes('401') || msg.includes('Unauthorized') || msg.includes('login')) {
        userMessage = 'Session expired. Please log in again.';
      } else {
        userMessage = msg || 'An unknown error occurred.';
      }

      setProcessingStatus(`❌ ${userMessage}`);
      setStatusType('danger');
      setIsLoading(false);
      alert(`Report generation failed:\n\n${userMessage}`);
    }
  };

  const handleDownload = (url, filename) => {
    try {
      console.log('Downloading:', filename, 'from presigned URL');
      
      // For presigned S3 URLs, open directly in new tab
      // The browser will handle the download based on content-type
      window.open(url, '_blank');
      
      console.log('Download initiated:', filename);
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download ${filename}. Please try again or contact support.\n\nError: ${error.message}`);
    }
  };

  const handleCancel = () => {
    stopPolling();
    setSelectedFile(null);
    setDownloadLinks([]);
    setProcessingStatus('');
    setIsLoading(false);
    navigate(-1);
  };

  return (
    <div className="container-fluid mt-4">
      <h1>Tribe Classification Reports</h1>
      <p className="text-muted">Upload an Excel file with incident data to generate tribe-specific reports</p>

      <form id="classificationForm" className="card p-4 mb-4" onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="excel_file" className="form-label">
            Select Excel File: <span className="text-danger">*</span>
          </label>
          <input
            type="file"
            id="excel_file"
            className="form-control"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={isLoading}
            required
          />
          <small className="form-text text-muted">
            Upload an Excel file (.xlsx or .xls) containing incident data with "Assignment group" column (max 10MB)
          </small>
          {selectedFile && (
            <div className="mt-2">
              <span className="badge bg-info">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </span>
            </div>
          )}
        </div>
        
        {processingStatus && (
          <div className={`alert alert-${statusType === 'warning' ? 'warning' : statusType} mb-3`} role="alert">
            {processingStatus}
            {isLoading && (
              <span className="ms-2 text-muted" style={{ fontSize: '0.85rem' }}>
                ({elapsedSeconds}s elapsed)
              </span>
            )}
          </div>
        )}
        
        <div className="d-flex gap-2">
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={isLoading || !selectedFile}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {elapsedSeconds > 5 ? 'Processing…' : 'Uploading…'}
              </>
            ) : statusType === 'danger' ? (
              'Retry'
            ) : (
              'Generate Reports'
            )}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Download Links Section */}
      {downloadLinks.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3">Download Reports</h3>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Tribe</th>
                  <th>PDF Report</th>
                  <th>CSV Data</th>
                </tr>
              </thead>
              <tbody>
                {downloadLinks.map((link, index) => (
                  <tr key={index}>
                    <td className="align-middle">
                      <strong>{link.tribe}</strong>
                    </td>
                    <td className="align-middle">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDownload(link.pdf_url, link.pdf_filename)}
                      >
                        <i className="bi bi-file-pdf me-1"></i>
                        Download PDF
                      </button>
                    </td>
                    <td className="align-middle">
                      {link.csv_url ? (
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => handleDownload(link.csv_url, link.csv_filename)}
                        >
                          <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                          Download CSV
                        </button>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="alert alert-warning mt-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Note:</strong> Download links expire after 1 hour. Please download all files you need.
          </div>
        </div>
      )}
    </div>
  );
}

export default Classification;